import React, { useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { pipe, Option, Array as A } from 'effect';

import Avatar from '@mui/material/Avatar';
import InputAdornment from '@mui/material/InputAdornment';
import type { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';

import AutocompleteVirtualizedBase from '@/components/mui/Form/Autocomplete/AutocompleteVirtualizedBase';
import { TextField } from '@/components/mui/Form/Autocomplete/components/styledComponents';
import TruncatedTooltip from '@/components/mui/Tooltip/TruncatedTooltip';
import helperTextError from '@/components/mui/Form/TextField/helperTextError';
import { MAX_VISIBLE_OPTIONS } from '@/components/mui/Form/Autocomplete/visibleOptions';
import type { PayingAccountField, PayingAccountFieldOption } from '@/domain/paySettings/schemas';
import { getOptionLogo, withLogoSrc } from '@/lib/optionLogo';
import useBankBranchEntry from '@/hooks/useBankBranchEntry';

import {
  getOptionDisplayLabel,
  getParentFieldName,
  getScopedOptions,
  readString,
} from '@/pages/dokkaPay/pages/configurations/company/pages/paymentProviders/components/Providers/components/DialogPaymentProviderDetails/components/DialogAddGlobalAccount/components/PayingAccountForm/utils';

const NO_MATCH_MESSAGE: Record<string, { id: string; defaultMessage: string }> = {
  bank: {
    id: 'configurations.company.paymentProviders.detailsModal.accountList.addModal.bank.noMatches',
    defaultMessage: 'No banks matching your search',
  },
};

type DisplayOption = PayingAccountFieldOption & { src?: string };

const measureInput = (el: HTMLElement) => el.querySelector('input');

type Props = {
  field: PayingAccountField;
  disabled: boolean;
  onValueCommitted?: (fieldName: string) => void;
};

const AutocompleteField: React.FC<Props> = ({ field, disabled, onValueCommitted }) => {
  const { formatMessage } = useIntl();
  const { control } = useFormContext();

  const parentName = getParentFieldName(field);
  const values = useWatch({ control });
  const parentText = parentName ? readString(values, parentName) : '';

  // Memoised: a new array identity rebuilds the virtualized listbox.
  const scopedOptions = useMemo<DisplayOption[]>(
    () =>
      pipe(
        getScopedOptions(field, parentName ? { [parentName]: parentText } : {}),
        withLogoSrc,
        A.map((option) => ({ ...option, label: getOptionDisplayLabel(option) })),
      ),
    [field, parentName, parentText],
  );
  // Bank options get a logo avatar (placeholder when `logo_url` is missing).
  const isBankField = useMemo(
    () => A.some(field.options ?? [], (option) => 'bank_code' in option || 'logo_url' in option),
    [field.options],
  );

  const branchEntry = useBankBranchEntry({
    fieldName: field.name,
    options: scopedOptions,
    value: readString(values, field.name),
    parentValue: parentText,
  });
  const { options } = branchEntry;

  const noOptionsText = useMemo(() => {
    if (!parentName) {
      return pipe(
        Option.fromNullable(NO_MATCH_MESSAGE[field.name]),
        Option.map((message) => formatMessage(message)),
        Option.getOrElse(() => formatMessage({ id: 'options.noOptionsText', defaultMessage: 'No options' })),
      );
    }
    if (branchEntry.noMatchText) return branchEntry.noMatchText;
    return parentText
      ? formatMessage({
          id: 'configurations.company.paymentProviders.detailsModal.accountList.addModal.branch.noBranches',
          defaultMessage: 'No branches found for this bank',
        })
      : formatMessage({
          id: 'configurations.company.paymentProviders.detailsModal.accountList.addModal.branch.selectBankFirst',
          defaultMessage: 'Select a bank first',
        });
  }, [parentName, parentText, field.name, formatMessage, branchEntry.noMatchText]);

  return (
    <Controller
      name={field.name}
      control={control}
      render={({ field: f, fieldState }) => {
        const current = readString({ [field.name]: f.value }, field.name);

        // Scoped list only — branch codes repeat across banks.
        const value: DisplayOption | null = branchEntry.findSelected(current);

        const setValue = (next: string) => {
          f.onChange(next);
          if (next !== current) onValueCommitted?.(field.name);
        };

        const textFieldProps = {
          label: field.label,
          size: 'small' as const,
          required: true,
          error: !!fieldState.error,
          helperText: helperTextError(fieldState.error?.message),
          onBlur: f.onBlur,
        };

        // Selected bank: logo + text in the input, as in the beneficiary dialog (no chip — single select).
        const renderInput = (params: AutocompleteRenderInputParams) => (
          <TruncatedTooltip title={value?.label ?? ''} measure={measureInput}>
            <TextField
              {...params}
              {...textFieldProps}
              InputProps={{
                ...params.InputProps,
                startAdornment:
                  isBankField && value ? (
                    <InputAdornment position="start">
                      <Avatar
                        alt={value.label}
                        src={pipe(getOptionLogo(value), Option.getOrUndefined)}
                        sx={{ width: 24, height: 24 }}
                      />
                    </InputAdornment>
                  ) : (
                    params.InputProps.startAdornment
                  ),
              }}
            />
          </TruncatedTooltip>
        );

        return (
          <AutocompleteVirtualizedBase
            // MUI keeps its input text when `value` is cleared programmatically.
            key={parentName ? `${field.name}:${parentText}` : field.name}
            fullWidth
            size="small"
            options={options}
            value={value}
            disabled={disabled}
            noOptionsText={noOptionsText}
            slotProps={{
              listbox: {
                suppressAvatar: !isBankField,
                suppressToolTip: false,
                tooltipOnTruncation: true,
                maxVisibleItems: MAX_VISIBLE_OPTIONS,
              },
            }}
            getOptionLabel={(option: DisplayOption) => option.label}
            onChange={(_: unknown, option: DisplayOption | null) => setValue(option?.value ?? '')}
            {...branchEntry.autocompleteProps}
            textFieldProps={textFieldProps}
            renderInput={renderInput}
          />
        );
      }}
    />
  );
};

export default AutocompleteField;
